<template>
  <div
    class="AtlasSelect"
    :class="{ 'is-disabled': isDisabled }"
    :tabindex="isDisabled ? undefined : 0"
    v-click-outside="close"
  >
    <div
      class="AtlasSelect-value"
      :class="textValue ? '' : 'AtlasSelect-value--placeholder'"
      @click.prevent="toggle"
    >
      {{ textValue || placeholderText }}
      <atlas-icon class="AtlasSelect-icon" name="select-arrow" :size="10" />
    </div>

    <ul
      class="AtlasSelect-options"
      v-if="isOpen && extendedOptions.length"
      ref="options"
      :style="{ maxHeight: optionsMaxHeight }"
    >
      <li
        class="AtlasSelect-option"
        v-for="option in extendedOptions"
        :key="option.id"
      >
        <router-link :to="option.to" custom v-slot="{ href }">
          <a
            class="AtlasSelect-optionTrigger"
            :class="{ 'is-active': option.isActive }"
            :href="href"
            @click="onOptionClick(option, $event)"
          >
            {{ option.text }}
          </a>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent, nextTick } from 'vue';

export default defineComponent({
  name: 'atlas-select',

  emits: ['choose'],

  props: {
    options: {
      type: Array,
      default: () => [],
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    optionsMaxHeight: null,
    isOpen: false,
    placeholderText: 'Please select…',
  }),

  computed: {
    activeOption() {
      return this.options.find((option) => option.isActive);
    },

    textValue() {
      return this.activeOption && this.activeOption.text;
    },

    isDisabled() {
      return this.disabled || this.options.length < 1;
    },

    extendedOptions() {
      return this.options.map((option) => {
        let { to } = option;
        if (!to) {
          to = option.href ? { path: option.href } : '';
        }

        return {
          ...option,
          to,
        };
      });
    },
  },

  watch: {
    isOpen(isOpen) {
      if (isOpen) {
        nextTick(() => {
          this.setOptionsMaxHeight();
        });
      }
    },
  },

  methods: {
    onOptionClick(option, event) {
      if (!option.to) event.preventDefault();

      if (option.isActive) return;

      this.$emit('choose', { option, event });
      this.close();
    },

    open() {
      if (!this.isDisabled) this.isOpen = true;
    },

    close() {
      this.isOpen = false;
    },

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    },

    setOptionsMaxHeight() {
      if (!this.$refs.options) {
        this.optionsMaxHeight = null;
      } else {
        const { top } = this.$refs.options.getBoundingClientRect();
        this.optionsMaxHeight = `${window.innerHeight - top - 15}px`;
      }
    },
  },
});
</script>

<style lang="scss">
.AtlasSelect {
  position: relative;

  &:focus {
    outline: none;
  }

  &.is-disabled {
    opacity: 0.5;
  }

  &-value,
  &-options {
    font-size: 18px;
    font-weight: bold;
    line-height: 1.15;
  }

  &-value {
    position: relative;
    overflow: hidden;
    width: 100%;
    padding: 1px 30px 10px 0;
    border-bottom: solid 1px #d5dae3;
    cursor: pointer;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--placeholder {
      font-weight: normal;
    }

    &.is-disabled {
      cursor: default;
    }

    .AtlasSelect:focus & {
      border-bottom-style: dotted;
    }
  }

  &-icon {
    @include position(absolute, 8px 5px null null);
  }

  &-options {
    @include elevation;
    @include antialias;
    @include position(absolute, -14px -15px null);
    z-index: z(options);
    box-sizing: border-box;
    padding: 0;
    padding: 10px 15px;
    margin: 0;
    background-color: color(black);
    overflow-y: auto;
  }

  &-option {
    display: block;
    padding: 0;
    border-bottom: solid 1px #7c8da1;
    margin: 0;

    &:last-child {
      border-bottom: 0;
    }
  }

  &-optionTrigger {
    display: block;
    padding: 5px 0;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.3s;

    &.is-active {
      cursor: default;
    }
  }

  .AtlasSelect-option .AtlasSelect-optionTrigger {
    // Has to override `.Indicator a`, hence the extra specificity
    color: color(white);

    &:hover,
    &.is-active {
      color: color(primary);
    }
  }
}
</style>
